/**
 * IncrementableInput — numeric stepper with +/− buttons.
 *
 * Thin rendering layer over `useIncrementableInput`. All state logic
 * (value parsing, increment/decrement, button disabled states) lives
 * in the hook — this component just handles presentation.
 *
 * For custom layouts, use `useIncrementableInput` directly and render
 * your own UI.
 */
import { memo, type FC } from "react";
import { Pressable, TextInput } from "react-native";
import "../../theme";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View } from "../View";
import { Text } from "../Text";
import { useIncrementableInput, type UseIncrementableInputOptions } from "./useIncrementableInput";

export interface IncrementableInputProps extends UseIncrementableInputOptions {
  /** Shows error styling (red border) on the input. */
  readonly error?: boolean | undefined;
  /** Helper or error text displayed below the input. */
  readonly helperText?: string | undefined;
  /** Max character length of the text input. Defaults to 2. */
  readonly maxLength?: number | undefined;
}

const IncrementableInput: FC<IncrementableInputProps> = memo(
  ({ error = false, helperText, maxLength = 2, ...hookOptions }) => {
    const { theme } = useUnistyles();
    const {
      displayText,
      isDecrementDisabled,
      isIncrementDisabled,
      handleChangeText,
      handleBlur,
      handleIncrement,
      handleDecrement,
    } = useIncrementableInput(hookOptions);

    const borderColor = error ? theme.colors.status.error : theme.colors.field.border_default;

    return (
      <View style={styles.root}>
        <View flexDirection="row">
          <Pressable
            onPress={handleDecrement}
            disabled={isDecrementDisabled}
            accessibilityRole="button"
            accessibilityLabel="Decrement"
            style={[
              styles.operatorButton,
              isDecrementDisabled ? styles.operatorButtonDisabled : undefined,
              {
                borderTopLeftRadius: theme.spacing.xl,
                borderBottomLeftRadius: theme.spacing.xl,
                borderRightWidth: 0,
                userSelect: "none",
              } as never,
            ]}
          >
            <Text textStyle="lg/regular" color="text" style={{ lineHeight: 18 }}>
              -
            </Text>
          </Pressable>
          <View style={[styles.inputContainer, { borderColor }]}>
            <TextInput
              value={displayText}
              onChangeText={handleChangeText}
              onBlur={handleBlur}
              editable={!hookOptions.disabled}
              keyboardType="numeric"
              maxLength={maxLength}
              hitSlop={10}
              accessibilityLabel="Numeric value"
              style={[styles.inputText, { outlineStyle: "none" } as never]}
            />
          </View>
          <Pressable
            onPress={handleIncrement}
            disabled={isIncrementDisabled}
            accessibilityRole="button"
            accessibilityLabel="Increment"
            style={[
              styles.operatorButton,
              isIncrementDisabled ? styles.operatorButtonDisabled : undefined,
              {
                borderTopRightRadius: theme.spacing.xl,
                borderBottomRightRadius: theme.spacing.xl,
                borderLeftWidth: 0,
                userSelect: "none",
              } as never,
            ]}
          >
            <Text textStyle="lg/regular" color="text" style={{ lineHeight: 18 }}>
              +
            </Text>
          </Pressable>
        </View>
        {helperText != null && (
          <Text textStyle="xs/regular" style={[styles.helperText, error && styles.helperTextError]}>
            {helperText}
          </Text>
        )}
      </View>
    );
  },
);

IncrementableInput.displayName = "IncrementableInput";

export { IncrementableInput };

const styles = StyleSheet.create((theme) => ({
  root: {
    flexShrink: 0,
    flexGrow: 0,
  },
  operatorButton: {
    padding: theme.spacing.xl,
    backgroundColor: theme.colors.foreground,
    borderColor: theme.colors.field.border_default,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    height: 48,
    width: 44,
  },
  operatorButtonDisabled: {
    opacity: 0.5,
  },
  inputContainer: {
    width: 44,
    height: 48,
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    overflow: "hidden",
  },
  inputText: {
    fontSize: 18,
    color: theme.colors.text,
    textAlign: "center",
    width: "100%",
    height: "100%",
  },
  helperText: {
    position: "absolute",
    top: "100%",
    left: theme.spacing.md,
    marginTop: theme.spacing.xs,
    color: theme.colors.textMuted,
  },
  helperTextError: {
    color: theme.colors.status.error,
  },
}));
